// Rows of the protein form's domain section: "Protein has <at least|exactly|at most> N copies
// of <text>" or "Protein has none of <text>". The server re-decides how the text is searched.
export const MAX_DOMAIN_ROWS = 3;

export const COMPARISONS = [
  { value: "atLeast", text: "at least" },
  { value: "exactly", text: "exactly" },
  { value: "atMost", text: "at most" },
  { value: "none", text: "none" },
];

let nextRowId = 0;

// The id only keys the row in the form; it is not sent.
export function newDomainRow(row = {}) {
  return { id: nextRowId++, comparison: "atLeast", count: 1, text: "", ...row };
}

// Same shapes the server accepts as accessions (server/src/solr/query.js).
const INTERPRO_ACCESSION = /^IPR\d{6}$/i;
const MEMBER_ACCESSION =
  /^(?:PF\d{5}|SM\d{5}|PS\d{5}|PR\d{5}|PD\d{6}|PTHR\d+|cd\d{5}|TIGR\d{5}|PIRSF\d+|NF\d+|MF_\d{5}|SFLD[A-Z]\d+|SSF-?\d+|(?:G3DSA:)?\d+(?:\.\d+){3})$/i;
const DATABASE_NAMES = new Set([
  "alphafold", "blastprodom", "cdd", "gene3d", "hamap", "hmmpfam", "hmmpir",
  "hmmsmart", "hmmtigr", "interpro", "mobidblite", "ncoils", "panther",
  "patternscan", "pfam", "prints", "profile", "profilescan", "prodom",
  "prosite_profiles", "seg", "sfld", "smart", "superfam", "superfamily",
  "tigrfam", "tmhmm",
]);

// "empty", "interpro" (an InterPro ID), "accession" (another database's ID) or "name".
export function domainTextKind(text) {
  const typed = String(text == null ? "" : text).trim();
  if (!typed) return "empty";
  // "INTERPRO PF02358": the database word is accepted, not enforced.
  const afterDatabase = typed.match(/^([A-Za-z0-9_]+)[\s:]+(\S+)$/);
  const candidate =
    afterDatabase && DATABASE_NAMES.has(afterDatabase[1].toLowerCase())
      ? afterDatabase[2]
      : typed;
  if (INTERPRO_ACCESSION.test(candidate)) return "interpro";
  return MEMBER_ACCESSION.test(candidate) ? "accession" : "name";
}

// What the form tells the user it understood about a row.
export function readingFor(row) {
  const kind = domainTextKind(row.text);
  const text = String(row.text).trim();
  if (kind === "empty") {
    return { note: "Type a domain or family name, or an ID from any database." };
  }
  const none = row.comparison === "none";
  if (kind === "name") {
    return {
      note:
        `Reading this as a name: finds domains named “${text}” in any database.` +
        (none ? " Proteins with such a domain are left out." : ""),
    };
  }
  const reading = {
    note:
      `Reading this as ${kind === "interpro" ? "an InterPro ID" : "an ID"}: exact match.` +
      (none ? " Proteins with this domain are left out." : ""),
  };
  const countsCopies = !none && !(row.comparison === "atLeast" && Number(row.count) <= 1);
  if (kind === "interpro" && countsCopies) {
    reading.warning =
      "Counting copies? An InterPro ID can count one domain several times, once for each database that found it. For an accurate count, use a Pfam ID.";
  }
  return reading;
}

// Rows with text become the request's domainSection; rows left empty are ignored.
export function domainRowsToParams(rows) {
  const live = rows.filter((row) => String(row.text).trim() !== "");
  if (live.length > 0 && live.every((row) => row.comparison === "none")) {
    return { error: "Add at least one domain the proteins must have. “None” rows can only narrow a search." };
  }
  const domainSection = live.map((row) => {
    const counts = domainTextKind(row.text) !== "name" && row.comparison !== "none";
    return {
      text: String(row.text).trim(),
      // A name has no copies to count; it is either held or, with "none", not.
      comparison: counts || row.comparison === "none" ? row.comparison : "atLeast",
      count: counts ? Math.max(1, parseInt(row.count, 10) || 1) : 1,
    };
  });
  return { domainSection };
}

// "has at least 2 copies of PF00076", "has no domain named TIR" (text already escaped by the caller).
export function describeDomainRow(row, highlight = (text) => text) {
  const text = highlight(row.text);
  if (row.comparison === "none") {
    return domainTextKind(row.text) === "name"
      ? `has no domain named ${text}`
      : `has no ${text}`;
  }
  if (domainTextKind(row.text) === "name") return `has a domain named ${text}`;
  const count = Number(row.count);
  if (row.comparison === "atLeast" && count <= 1) return `has ${text}`;
  const words = { atLeast: "at least", exactly: "exactly", atMost: "at most" }[row.comparison];
  return `has ${words} ${count} ${count === 1 ? "copy" : "copies"} of ${text}`;
}

// Shown when a search that named a domain finds nothing.
export function noMatchHint(domainSection) {
  const name = (domainSection || []).find(
    (row) => row.comparison !== "none" && domainTextKind(row.text) === "name"
  );
  if (!name) return "";
  return `No proteins have a domain name with the whole word “${name.text}”. Names match whole words only. Try the full word (for example “kinase”) or a Pfam or InterPro ID.`;
}
